import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useUserContext } from "../contexts/UserContext";
import { apiGet } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";

/** ✅ Firebase の custom claims から super 判定（tenant に依存しない） */
async function isFirebaseSuper(timeoutMs = 5000): Promise<boolean> {
    const auth = getAuth();
    let u = auth.currentUser;

    if (!u) {
        u = await new Promise<any>((resolve) => {
            const unsub = onAuthStateChanged(auth, (user) => {
                if (user) {
                    unsub();
                    resolve(user);
                }
            });
            setTimeout(() => {
                unsub();
                resolve(null);
            }, timeoutMs);
        });
    }

    if (!u) return false;

    // ✅ 最小修正：claims 反映遅延対策（強制リフレッシュ）
    const tokenResult = await u.getIdTokenResult(true).catch(() => null as any);
    const claims = tokenResult?.claims || {};
    if (claims.superAdmin === true || claims.superAdminGlobal === true) return true;

    // tenant単位superはclaimsに出ないケースがあるため /api/me も確認
    try {
        const token = tokenResult?.token || (await u.getIdToken());
        const tenantId = localStorage.getItem("selectedTenant") || "ecvision";
        const res = await fetch("/api/me", {
            method: "GET",
            cache: "no-store",
            headers: {
                Authorization: `Bearer ${token}`,
                "X-Tenant-Id": tenantId,
            },
        });
        if (!res.ok) return false;
        const me: any = await res.json().catch(() => ({}));
        return (
            me?.superAdmin === true ||
            me?.superAdminGlobal === true ||
            (Array.isArray(me?.superAdminTenants) && me.superAdminTenants.length > 0)
        );
    } catch {
        return false;
    }
}

function sanitizeRedirect(raw: string): string {
    const p = String(raw || "").trim();
    if (!p) return "";
    // オープンリダイレクト対策：相対パスのみ許可
    if (!p.startsWith("/") || p.startsWith("//")) return "";
    // ループ防止
    if (p.startsWith("/login") || p.startsWith("/check-status")) return "";
    return p;
}

function getRedirectCandidate(): string {
    if (typeof window === "undefined") return "";
    const sp = new URLSearchParams(window.location.search);
    const next = sp.get("next");
    const fromQuery = next ? decodeURIComponent(next) : "";
    const fromLS = localStorage.getItem("postLoginRedirect") || "";
    return sanitizeRedirect(fromQuery || fromLS);
}

function clearRedirect() {
    localStorage.removeItem("postLoginRedirect");
}

export default function CheckStatusPage() {
    const user = useUserContext();
    const navigate = useNavigate();
    const once = useRef(false);

    useEffect(() => {
        if (once.current) return;

        const path = typeof window !== "undefined" ? window.location.pathname : "";
        console.log("path=" + path);

        const post = localStorage.getItem("postLoginRedirect") || "";
        const wantsSuper = post.startsWith("/super");

        // ✅ /super 以外のリダイレクト先（/app/itemCost など）
        const redirect = getRedirectCandidate();
        const shouldRedirect = !!redirect && !redirect.startsWith("/super");

        console.log("wantsSuper=" + wantsSuper, "redirect=" + redirect);

        // ✅ (A) /super に戻したい意図があるなら最優先（useUserに依存しない）
        if (wantsSuper) {
            once.current = true;

            const backTo = post || "/super";
            clearRedirect();

            (async () => {
                const ok = await isFirebaseSuper();
                if (ok) navigate(backTo, { replace: true });
                else navigate("/login", { replace: true });
            })();

            return;
        }

        // ✅ /super 直アクセス保険（useUserに依存しない）
        // ※通常ここは /check-status なのでほぼ通らないが、保険として残す
        if (path.startsWith("/super")) {
            once.current = true;
            (async () => {
                const ok = await isFirebaseSuper();
                if (ok) navigate("/super", { replace: true });
                else navigate("/login", { replace: true });
            })();
            return;
        }

        // ✅ テナント分岐（なければ ecvision 扱い）
        const selectedTenant = localStorage.getItem("selectedTenant") || "ecvision";

        // =========================
        // ✅ ecx（ECX JWT）ルート
        // =========================
        if (selectedTenant === "ecx") {
            console.log("★1");

            const token = localStorage.getItem("ecx_access_token");

            if (!token) {
                console.log("★2");

                // ✅ 重要：ここで once を立てて return しないと、下の ecvision ルートが走って /app に吸われる
                once.current = true;

                (async () => {
                    try {
                        const res = await apiGet("/api/me/company");
                        console.log("res:", res);
                        if (!res.ok) {
                            navigate("/login", { replace: true });
                            return;
                        }

                        const me: any = await res.json();
                        user.setCompanyId(me.companyId);

                        if (shouldRedirect) {
                            clearRedirect();
                            navigate(redirect, { replace: true });
                        } else {
                            clearRedirect();
                            navigate("/app", { replace: true });
                        }
                    } catch (e) {
                        console.error("[ecx check-status] failed:", e);
                        navigate("/ecx/login", { replace: true });
                    }
                })();

                return; // ✅ ここが必須
            } else {
                console.log("★3");
                once.current = true; // ← ECXは user.loading に依存しない

                (async () => {
                    try {
                        const res = await fetch("/api/ecx/me/company", {
                            method: "GET",
                            headers: {
                                "Authorization": `Bearer ${token}`,
                                "x-tenant-id": "ecx",
                            },
                            cache: "no-store",
                        });

                        if (!res.ok) {
                            const errorBody = await res.text().catch(() => "");
                            console.error("[ecx check-status] api returned error", {
                                status: res.status,
                                statusText: res.statusText,
                                body: errorBody.slice(0, 500),
                            });
                            navigate("/ecx/login", { replace: true });
                            return;
                        }

                        let me: any;
                        try {
                            me = await res.json();
                        } catch (parseErr) {
                            console.error("[ecx check-status] response parse failed", {
                                error: String(parseErr),
                                status: res.status,
                            });
                            navigate("/ecx/login", { replace: true });
                            return;
                        }

                        console.log("[ecx check-status] me/company response", {
                            companyId: me.companyId,
                            tenantId: me.tenantId,
                            uid: me.uid?.slice(0, 20),
                        });

                        // ✅ 初回は companyId が null → /ecx/link
                        if (!me.companyId) {
                            console.log("[ecx check-status] redirecting to /ecx/link (no company)");
                            navigate("/ecx/link", { replace: true });
                            return;
                        }

                        user.setCompanyId(me.companyId);

                        if (shouldRedirect) {
                            clearRedirect();
                            navigate(redirect, { replace: true });
                        } else {
                            clearRedirect();
                            navigate("/app", { replace: true });
                        }
                    } catch (e: any) {
                        console.error("[ecx check-status] exception", {
                            name: e?.name,
                            message: e?.message,
                            stack: e?.stack?.split("\n").slice(0, 3),
                        });
                        navigate("/ecx/login", { replace: true });
                    }
                })();

                return;
            }
        }

        // =========================
        // ✅ ecvision（Firebase）ルート
        // =========================
        if (user.loading) return;

        once.current = true;

        // tenant mismatch は login へ（CompanyGate でもやってるが保険）
        if ((user as any).error === "tenant_mismatch") {
            navigate("/login", { replace: true });
            return;
        }

        // pending があれば /pending（プロパティが無い実装でも安全）
        if ((user as any).status === "pending") {
            navigate("/pending", { replace: true });
            return;
        }

        // ✅ tenant単位superも super と同等扱い
        if (
            (user as any).superAdmin ||
            (user as any).superAdminGlobal ||
            (Array.isArray((user as any).superAdminTenants) && (user as any).superAdminTenants.length > 0)
        ) {
            navigate("/super", { replace: true });
            return;
        }

        if (!user.companyId) {
            // 会社未選択なら従来どおり（ここでは postLoginRedirect を消さない）
            navigate("/select-company", { replace: true });
            return;
        }

        // ✅ 会社があるなら redirect を最優先
        if (shouldRedirect) {
            clearRedirect();
            navigate(redirect, { replace: true });
            return;
        }

        // ✅ fallback
        clearRedirect();
        navigate("/app", { replace: true });
    }, [user.loading, user, navigate]);

    return <LoadingInline />;
}
